import {
  Component,
  DestroyRef,
  ElementRef,
  OnDestroy,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { catchError, of } from 'rxjs';

import { SAVING_ARENA_API_CONFIG } from '../../../../core/api/api.config';
import {
  FrontendBlog,
  FrontendBlogCategory,
  FrontendBlogsPage,
} from '../../../../core/api/api.models';
import { blogBodyText, blogReadMinutes } from '../../../../core/api/blog-text';
import { SavingArenaApiService } from '../../../../core/api/saving-arena-api.service';
import { TrustedCmsHtmlPipe } from '../../../../core/api/trusted-cms-html.pipe';
import { SeoService } from '../../../../core/seo/seo.service';
import { breadcrumbSchemaFor, breadcrumbTrail } from '../../../../core/seo/breadcrumb-trail';
import { webPageSchema } from '../../../../core/seo/structured-data';
import { Breadcrumb } from '../../../../shared/breadcrumb/breadcrumb';
import { BigSaleBanner } from '../../../../shared/big-sale-banner/big-sale-banner';
import { BlogPostCard } from '../../../../shared/blog-post-card/blog-post-card';

const BLOGS_PATH = '/blogs';
const DEFAULT_BLOGS_TITLE = 'Savings Blog: Coupon Guides & Shopping Tips | SavingArena';
const DEFAULT_BLOGS_DESCRIPTION =
  'Coupon guides, store deep dives and seasonal savings strategies from the SavingArena editorial team.';

interface PopularCategoryItem {
  label: string;
  routeLink: string[];
  imageUrl: string;
}

interface FaqItem {
  question: string;
  answer: string;
}

interface BlogPostView {
  id: string;
  title: string;
  excerpt: string;
  imageUrl: string;
  storeLogoUrl: string;
  routeLink: string[];
  categoryName: string;
  categoryRouteLink: string[];
  authorName: string;
  authorRouteLink: string[];
  readTime: string;
  readTimeLong: string;
}

@Component({
  selector: 'app-blog-home-page',
  imports: [Breadcrumb, RouterLink, TrustedCmsHtmlPipe, BigSaleBanner, BlogPostCard],
  templateUrl: './blog-home-page.html',
  styleUrl: './blog-home-page.scss',
})
export class BlogHomePage implements OnDestroy {
  protected readonly crumbs = breadcrumbTrail({ label: 'Blog' });

  private readonly api = inject(SavingArenaApiService);
  private readonly apiConfig = inject(SAVING_ARENA_API_CONFIG);
  private readonly destroyRef = inject(DestroyRef);
  private readonly seo = inject(SeoService);
  private popularCategoryAutoplayTimer: ReturnType<typeof setInterval> | null = null;

  // Scroll target so paging jumps back to the top of the posts grid.
  @ViewChild('postsTop') private readonly postsTop?: ElementRef<HTMLElement>;

  // Admin-managed content (Settings › Page Settings › Blogs Page). Empty until saved once, in
  // which case the built-in defaults in the template remain in place.
  protected readonly pageH1 = signal('');
  protected readonly pageShortDescription = signal('');
  protected readonly pageH2 = signal('');
  protected readonly descriptionHtml = signal('');
  protected readonly adminFaqs = signal<FaqItem[]>([]);
  private adminCategoriesActive = false;

  protected readonly popularCardsPerSlide = 5;
  protected readonly postsPerPage = 15;
  protected readonly blogPosts = signal<BlogPostView[]>([]);
  protected readonly popularCategories = signal<PopularCategoryItem[]>([]);
  protected readonly currentPopularCategorySlide = signal(0);
  protected readonly currentPostsPage = signal(1);
  protected readonly faqItems: FaqItem[] = [
    {
      question: 'How do I use a coupon code on SavingArena?',
      answer:
        "Browse our catalog of coupons and deals, click on the one you want to use, and copy the code. Then shop at the retailer's website and paste the code at checkout to apply your savings.",
    },
    {
      question: 'Are the coupons on SavingArena really free?',
      answer:
        'Yes! All coupons and deals on SavingArena are completely free to use. We partner with retailers to bring you exclusive discounts without any hidden fees or subscription requirements.',
    },
    {
      question: 'How often are the deals and coupons updated?',
      answer:
        'Our team refreshes deals daily to ensure you have access to the latest discounts. We remove expired codes automatically and add new ones as soon as they become available from our retail partners.',
    },
    {
      question: 'Do I need to create an account to use SavingArena?',
      answer:
        'No account is required. You can browse and copy coupon codes immediately. However, creating a free account gives you access to personalized deal alerts, price drop notifications, and a saved wishlist.',
    },
    {
      question: 'How does SavingArena make money?',
      answer:
        "We earn a small commission from retailers when you make a purchase using our links or codes. This doesn't affect the savings you get — in fact, it allows us to keep the service free for everyone.",
    },
    {
      question: "What should I do if a coupon code doesn't work?",
      answer:
        "If a code doesn't work, first double-check that you've entered it correctly. Some codes have minimum purchase requirements or exclusions. If it still fails, please report it so we can verify or replace it promptly.",
    },
  ];
  protected readonly expandedFaqIndex = signal<number | null>(null);

  // FAQ list actually rendered: admin FAQs when configured, otherwise the built-in defaults.
  protected readonly displayedFaqs = computed(() =>
    this.adminFaqs().length > 0 ? this.adminFaqs() : this.faqItems,
  );

  protected readonly featuredPosts = computed(() => this.blogPosts().slice(0, 3));
  protected readonly primaryFeaturedPost = computed(() => this.featuredPosts()[0] ?? null);
  protected readonly secondaryFeaturedPosts = computed(() => this.featuredPosts().slice(1, 3));
  protected readonly paginatedPosts = computed(() => {
    const start = 3 + (this.currentPostsPage() - 1) * this.postsPerPage;
    return this.blogPosts().slice(start, start + this.postsPerPage);
  });
  protected readonly postsTotalPages = computed(() =>
    Math.max(1, Math.ceil(Math.max(0, this.blogPosts().length - 3) / this.postsPerPage)),
  );
  protected readonly postsPageNumbers = computed(() =>
    Array.from({ length: this.postsTotalPages() }, (_, index) => index + 1),
  );

  // Windowed pager: shows first/last, the current page and its neighbours, with 'ellipsis' gaps —
  // so the control stays compact no matter how many pages of blogs exist.
  protected readonly postsPageItems = computed<(number | 'ellipsis')[]>(() => {
    const total = this.postsTotalPages();
    const current = this.currentPostsPage();

    if (total <= 7) {
      return Array.from({ length: total }, (_, index) => index + 1);
    }

    const pages = [1, total, current, current - 1, current + 1]
      .filter((page) => page >= 1 && page <= total)
      .sort((a, b) => a - b);
    const unique = [...new Set(pages)];

    const items: (number | 'ellipsis')[] = [];
    let previous = 0;
    for (const page of unique) {
      if (page - previous > 1) {
        items.push('ellipsis');
      }
      items.push(page);
      previous = page;
    }
    return items;
  });

  protected readonly popularCategoryTotalSlides = computed(() =>
    Math.max(1, Math.ceil(this.popularCategories().length / this.popularCardsPerSlide)),
  );

  protected readonly popularCategorySlideIndexes = computed(() =>
    Array.from({ length: this.popularCategoryTotalSlides() }, (_, index) => index),
  );

  protected readonly visiblePopularCategories = computed(() => {
    const start = this.currentPopularCategorySlide() * this.popularCardsPerSlide;
    return this.popularCategories().slice(start, start + this.popularCardsPerSlide);
  });

  constructor() {
    this.loadBlogs();
    this.loadBlogCategories();
    this.loadPageSettings();
    this.startPopularCategoryAutoplay();
  }

  ngOnDestroy(): void {
    this.stopPopularCategoryAutoplay();
  }

  protected selectPopularCategorySlide(index: number): void {
    this.currentPopularCategorySlide.set(index);
    this.restartPopularCategoryAutoplay();
  }

  protected goToPostsPage(page: number): void {
    if (page < 1 || page > this.postsTotalPages() || page === this.currentPostsPage()) {
      return;
    }

    this.currentPostsPage.set(page);
    this.postsTop?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  protected goToPreviousPostsPage(): void {
    this.goToPostsPage(this.currentPostsPage() - 1);
  }

  protected goToNextPostsPage(): void {
    this.goToPostsPage(this.currentPostsPage() + 1);
  }

  protected toggleFaq(index: number): void {
    this.expandedFaqIndex.update((current) => (current === index ? null : index));
  }

  private loadBlogs(page = 1, accumulatedBlogs: FrontendBlog[] = []): void {
    this.api
      .getBlogs({ page, limit: 100, fields: 'card' })
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        if (!response) {
          this.blogPosts.set(this.toBlogPostViews(accumulatedBlogs));
          return;
        }

        const nextBlogs = [...accumulatedBlogs, ...response.data];

        if (response.pagination.page < response.pagination.pages) {
          this.loadBlogs(response.pagination.page + 1, nextBlogs);
          return;
        }

        this.blogPosts.set(this.toBlogPostViews(nextBlogs));
        this.currentPostsPage.set(1);
      });
  }

  private loadBlogCategories(): void {
    this.api
      .getBlogCategories()
      .pipe(
        catchError(() => of({ success: false, data: [] })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        // Admin-curated categories take precedence over the auto-derived latest list.
        if (this.adminCategoriesActive) {
          return;
        }
        this.popularCategories.set(this.toPopularCategories(response.data));
        this.currentPopularCategorySlide.set(0);
        this.restartPopularCategoryAutoplay();
      });
  }

  private loadPageSettings(): void {
    this.api
      .getBlogsPageSettings()
      .pipe(
        catchError(() => of({ success: false, data: null })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        this.applyBlogsPageSettings(response.data);
        // SEO is applied even when the admin settings are missing, so the page always ships a
        // title/description/canonical instead of falling back to the app shell defaults.
        this.applyBlogsSeo(response.data);
      });
  }

  private applyBlogsSeo(settings: FrontendBlogsPage | null | undefined): void {
    const title = (settings?.metaTitle ?? '').trim() || DEFAULT_BLOGS_TITLE;
    const description = (settings?.metaDescription ?? '').trim() || DEFAULT_BLOGS_DESCRIPTION;
    const url = this.seo.absolute(BLOGS_PATH);

    this.seo.apply({ title, description, canonicalPath: BLOGS_PATH });

    this.seo.setJsonLd([
      webPageSchema({ origin: this.seo.origin, url, name: title, description }),
      breadcrumbSchemaFor(this.crumbs, (p) => this.seo.absolute(p)),
    ]);
  }

  private applyBlogsPageSettings(settings: FrontendBlogsPage | null | undefined): void {
    if (!settings) {
      return;
    }

    this.pageH1.set((settings.h1 ?? '').trim());
    this.pageShortDescription.set((settings.shortDescription ?? '').trim());
    this.pageH2.set((settings.h2 ?? '').trim());
    this.descriptionHtml.set((settings.description ?? '').trim());

    const faqs = Array.isArray(settings.faqs)
      ? settings.faqs
          .map((faq) => ({
            question: (faq.question ?? '').trim(),
            answer: (faq.answer ?? '').trim(),
          }))
          .filter((faq) => faq.question && faq.answer)
      : [];
    this.adminFaqs.set(faqs);

    const categories = Array.isArray(settings.popularBlogCategories)
      ? settings.popularBlogCategories
      : [];
    if (categories.length > 0) {
      this.adminCategoriesActive = true;
      this.popularCategories.set(this.toPopularCategoriesFromAdmin(categories));
      this.currentPopularCategorySlide.set(0);
      this.restartPopularCategoryAutoplay();
    }
  }

  // Admin-selected popular categories, mapped preserving the chosen order (unlike the auto list).
  private toPopularCategoriesFromAdmin(categories: FrontendBlogCategory[]): PopularCategoryItem[] {
    return categories.map((category) => ({
      label: category.name,
      routeLink: ['/blogs', category.slug || this.slugify(category.name)],
      imageUrl: this.assetUrl(category.iconImagePath),
    }));
  }

  private toBlogPostViews(blogs: FrontendBlog[]): BlogPostView[] {
    return blogs
      .map((blog, index) => ({ blog, index }))
      .sort((a, b) => {
        const dateDiff = this.blogDateMs(b.blog) - this.blogDateMs(a.blog);
        return dateDiff === 0 ? a.index - b.index : dateDiff;
      })
      .map(({ blog }) => {
        const category = blog.category;
        const categoryName = category?.name ?? 'Shopping Tips';
        const categorySlug = category?.slug || this.slugify(categoryName);
        const readTimeMinutes = this.readTimeMinutes(blog);
        const store = Array.isArray(blog.stores) ? blog.stores[0] : undefined;
        const storeLogo =
          store && typeof store.logoImagePath === 'string' ? store.logoImagePath.trim() : '';

        return {
          id: blog.id,
          title: blog.name,
          excerpt:
            this.stripHtml(blog.shortDescription ?? '') ||
            this.excerptFromContent(blogBodyText(blog)) ||
            blog.name,
          imageUrl: this.assetUrl(blog.thumbnailImagePath) || '/images/blog-placeholder.svg',
          storeLogoUrl: storeLogo ? this.assetUrl(storeLogo) : '',
          routeLink: ['/blogs', blog.slug],
          categoryName,
          categoryRouteLink: ['/blogs', categorySlug],
          authorName: blog.author || 'SavingArena Editorial Team',
          authorRouteLink: ['/experts', this.slugify(blog.author || 'SavingArena Editorial Team')],
          readTime: `${readTimeMinutes} Min`,
          readTimeLong: `${readTimeMinutes} Min Read`,
        } satisfies BlogPostView;
      });
  }

  private toPopularCategories(categories: FrontendBlogCategory[]): PopularCategoryItem[] {
    return [...categories]
      .sort((a, b) => this.unknownDateMs(b['updatedAt']) - this.unknownDateMs(a['updatedAt']))
      .slice(0, 20)
      .map((category) => ({
        label: category.name,
        routeLink: ['/blogs', category.slug || this.slugify(category.name)],
        imageUrl: this.assetUrl(category.iconImagePath),
      }));
  }

  private blogDateMs(blog: FrontendBlog): number {
    return this.unknownDateMs(blog.publishAt) || this.unknownDateMs(blog.updatedAt);
  }

  private unknownDateMs(value: unknown): number {
    if (typeof value !== 'string') {
      return 0;
    }

    const dateMs = new Date(value).getTime();

    return Number.isNaN(dateMs) ? 0 : dateMs;
  }

  private readTimeMinutes(blog: FrontendBlog): number {
    return blogReadMinutes(blog);
  }

  private excerptFromContent(value?: string): string {
    if (!value) {
      return '';
    }

    const text = this.stripHtml(value);

    if (text.length <= 150) {
      return text;
    }

    return `${text.slice(0, 150).trimEnd()}...`;
  }

  private stripHtml(value: string): string {
    return value
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private assetUrl(path: string | undefined): string {
    if (!path) {
      return '';
    }

    if (/^https?:\/\//i.test(path)) {
      return path;
    }

    const assetBaseUrl = this.apiConfig.assetBaseUrl.replace(/\/$/, '');
    const assetPath = path.startsWith('/') ? path : `/${path}`;

    return `${assetBaseUrl}${assetPath}`;
  }

  private slugify(value: string): string {
    return value
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  private startPopularCategoryAutoplay(): void {
    if (this.popularCategoryAutoplayTimer !== null || this.popularCategoryTotalSlides() < 2) {
      return;
    }

    this.popularCategoryAutoplayTimer = setInterval(() => {
      const totalSlides = this.popularCategoryTotalSlides();
      this.currentPopularCategorySlide.update((index) => (index + 1) % totalSlides);
    }, 5000);
  }

  private stopPopularCategoryAutoplay(): void {
    if (this.popularCategoryAutoplayTimer === null) {
      return;
    }

    clearInterval(this.popularCategoryAutoplayTimer);
    this.popularCategoryAutoplayTimer = null;
  }

  private restartPopularCategoryAutoplay(): void {
    this.stopPopularCategoryAutoplay();
    this.startPopularCategoryAutoplay();
  }
}
